import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Download, Info } from 'lucide-react';

import { famBundlesApi } from 'api/famBundles';
import { UserContext } from 'contexts/UserContext';
import { Alert, AlertDescription } from 'shadcn/components/ui/Alert';
import { Button } from 'shadcn/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'shadcn/components/ui/Card';
import { Input } from 'shadcn/components/ui/Input';
import { Label } from 'shadcn/components/ui/Label';
import { cn } from 'shadcn/lib/utils';
import {
    BULK_UPLOAD_STATUS,
    BulkUploadDropzone,
    BulkUploadPendingChanges,
    BulkUploadRowFilterSwitch,
    BulkUploadStatus,
    BulkUploadTable,
    useBulkUpload,
} from 'shared/components/bulkUpload';

import { Nav } from '../../shared';

const BUNDLE_COLUMN_DEFS = [
    { key: 'listName', label: 'List Name', required: true },
    { key: 'facilityName', label: 'Facility', required: true },
    { key: 'sku', label: 'Sku', required: true },
    { key: 'substitution1', label: 'Substitution 1', required: false },
    { key: 'substitution2', label: 'Substitution 2', required: false },
];

function isValidWeek(value) {
    const n = Number(value);
    return Number.isInteger(n) && n >= 1 && n <= 53;
}

function isValidYear(value) {
    const n = Number(value);
    return Number.isInteger(n) && n >= 2000 && n <= 2100;
}

function BulkBundlesUpload() {
    const { famAccount } = useContext(UserContext);
    const source = famAccount?.partnershipSource || '';

    const [week, setWeek] = useState('');
    const [year, setYear] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [persistSuccess, setPersistSuccess] = useState(false);

    // Fetch week/year from lightweight endpoint
    useEffect(() => {
        if (!source) {
            return () => {};
        }
        let cancelled = false;

        famBundlesApi
            .getWeekYearForBundleCreation(source)
            .then((res) => {
                if (cancelled) return;
                if (res.status && res.data) {
                    if (res.data.week) setWeek(String(res.data.week));
                    if (res.data.year) setYear(String(res.data.year));
                }
            })
            .catch(() => {
                // Silently fail - user can still enter week/year manually
            });

        return () => {
            cancelled = true;
        };
    }, [source]);

    const api = useMemo(
        () => ({
            edit: (key, params, rowsToUpdate, rowsToDelete) =>
                famBundlesApi.bulkEdit(key, params, rowsToUpdate, rowsToDelete),
            fetch: (key, params, signal) => famBundlesApi.bulkFetch(key, params, signal),
            persist: (key, params) => famBundlesApi.bulkPersist(key, params),
            upload: (file, params) => famBundlesApi.bulkUpload(file, { ...params, week, year }),
        }),
        [week, year],
    );

    const params = useMemo(
        () => ({
            source,
        }),
        [source],
    );

    const bulkUpload = useBulkUpload({ api, params });

    const canUpload = Boolean(source) && isValidWeek(week) && isValidYear(year) && !!selectedFile;

    const handleDownloadTemplate = useCallback(() => {
        const headers = BUNDLE_COLUMN_DEFS.map((col) => `${col.label}${col.required ? '*' : ''}`).join(',');
        const exampleRow = {
            listName: 'Example List Name',
            facilityName: 'LAX1',
            sku: 'SKU-123',
            substitution1: 'SUB-SKU-1',
            substitution2: 'SUB-SKU-2',
        };
        const exampleValues = BUNDLE_COLUMN_DEFS.map((col) => exampleRow[col.key] ?? '').join(',');
        const csvContent = `${headers}\n${exampleValues}`;
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'fam_list_bulk_upload_template.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, []);

    const handleFileSelect = useCallback((file) => {
        setSelectedFile(file);
    }, []);

    const handleUploadSelectedFile = useCallback(() => {
        if (selectedFile && Boolean(source) && isValidWeek(week) && isValidYear(year)) {
            bulkUpload.uploadFile(selectedFile);
        }
    }, [bulkUpload, selectedFile, source, week, year]);

    const handlePersist = useCallback(async () => {
        try {
            const result = await bulkUpload.persist();
            const hasErrors = result?.persistResults?.errors?.length > 0;
            if (!hasErrors) {
                setPersistSuccess(true);
                setSelectedFile(null);
                bulkUpload.reset();
            }
            // If there are errors, they will be displayed via bulkUpload.persistErrors
        } catch {
            // Error is already captured in bulkUpload.persistError
        }
    }, [bulkUpload]);

    const handleStartNew = useCallback(() => {
        setPersistSuccess(false);
        setSelectedFile(null);
    }, []);

    const handleStartOver = useCallback(() => {
        setPersistSuccess(false);
        setSelectedFile(null);
        bulkUpload.reset();
    }, [bulkUpload]);

    const hasData = bulkUpload.rows.length > 0;
    const errorCount = bulkUpload.validationErrors.length;

    const weekYearErrors = bulkUpload.validationErrors.filter(
        (error) => error.field === 'week' || error.field === 'year',
    );
    const weekYearErrorStrings = [...new Set(weekYearErrors.map((error) => error.message))].join(', ');

    const errorCountExcludingDuplicateWeekYear =
        errorCount - weekYearErrors.length + (weekYearErrors.length > 0 ? 1 : 0);

    const infoMessages = useMemo(() => {
        const messages = [];
        if (source && isValidWeek(week) && isValidYear(year)) {
            messages.push(`Uploading for week ${week}, ${year}.`);
        }
        return messages;
    }, [source, week, year]);

    return (
        <div className="page-container">
            <Nav />
            <div className="content-container">
                <div className={cn('mt-4 space-y-6 width-100', hasData && 'flex min-h-[calc(100dvh-10rem)] flex-col')}>
                    <Card className={cn('w-full', hasData && 'flex min-h-0 min-w-0 flex-1 flex-col')}>
                        <CardHeader>
                            <CardTitle>Bulk Bundle Upload</CardTitle>
                            <CardDescription>
                                Upload a CSV file to create or edit multiple curated-list bundles at once. The file will
                                be validated before you can save.
                            </CardDescription>
                            <div className="text-sm text-destructive">
                                <div className="font-medium">Important:</div>
                                <ul className="list-disc space-y-1 pl-5">
                                    <li>
                                        If a list with the same List Name and Facility as a row in the file already
                                        exists for the selected week/year, this upload will overwrite it, and the
                                        existing list will become a Simple Bundle.
                                    </li>
                                    <li>If it doesn’t exist, a new Simple Bundle will be created.</li>
                                    <li>Simple Bundle Lists do not generate automatically week-to-week.</li>
                                </ul>
                            </div>
                        </CardHeader>
                        <CardContent className={cn('space-y-4', hasData && 'flex min-h-0 min-w-0 flex-1 flex-col')}>
                            {!hasData && !persistSuccess && (
                                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="fam-bundles-week">Week</Label>
                                        <Input
                                            id="fam-bundles-week"
                                            inputMode="numeric"
                                            placeholder="1-53"
                                            value={week}
                                            onChange={(e) => setWeek(e.target.value)}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="fam-bundles-year">Year</Label>
                                        <Input
                                            id="fam-bundles-year"
                                            inputMode="numeric"
                                            placeholder="e.g. 2026"
                                            value={year}
                                            onChange={(e) => setYear(e.target.value)}
                                        />
                                    </div>
                                </div>
                            )}

                            {!hasData && !persistSuccess && (
                                <>
                                    <div className="flex items-center justify-end gap-2">
                                        <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                                            <Download className="mr-2 h-4 w-4" />
                                            Download Template
                                        </Button>
                                    </div>
                                    <BulkUploadDropzone
                                        onFileSelect={handleFileSelect}
                                        disabled={[BULK_UPLOAD_STATUS.LOADING, BULK_UPLOAD_STATUS.UPLOADING].includes(
                                            bulkUpload.status,
                                        )}
                                        isUploading={bulkUpload.status === BULK_UPLOAD_STATUS.UPLOADING}
                                    />
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="default"
                                                size="sm"
                                                onClick={handleUploadSelectedFile}
                                                disabled={
                                                    !canUpload ||
                                                    [BULK_UPLOAD_STATUS.LOADING, BULK_UPLOAD_STATUS.UPLOADING].includes(
                                                        bulkUpload.status,
                                                    )
                                                }
                                            >
                                                Submit
                                            </Button>
                                        </div>
                                    </div>
                                </>
                            )}

                            <BulkUploadStatus
                                entityName="Bundle"
                                fetchError={bulkUpload.fetchError}
                                handleStartNew={handleStartNew}
                                hasData={hasData}
                                infoMessages={infoMessages}
                                persistError={bulkUpload.persistError}
                                persistErrors={bulkUpload.persistErrors}
                                persistSuccess={persistSuccess}
                                status={bulkUpload.status}
                                uploadError={bulkUpload.uploadError}
                            />

                            {hasData &&
                                !persistSuccess &&
                                !bulkUpload.hasPersistErrors &&
                                ![BULK_UPLOAD_STATUS.SUBMITTING, BULK_UPLOAD_STATUS.PERSISTING].includes(
                                    bulkUpload.status,
                                ) && (
                                    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
                                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                            <div>
                                                {errorCount > 0 ? (
                                                    <div className="text-sm text-destructive">
                                                        <div>
                                                            {errorCountExcludingDuplicateWeekYear} validation error
                                                            {errorCountExcludingDuplicateWeekYear !== 1 ? 's' : ''}{' '}
                                                            found. Please fix errors before saving.
                                                        </div>
                                                        <div>{weekYearErrorStrings}</div>
                                                    </div>
                                                ) : (
                                                    <div className="text-sm text-green-700">
                                                        No validation errors. Ready to save.
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
                                                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                                                    {bulkUpload.showIssuesFilterHint && (
                                                        <Alert
                                                            className="w-full max-w-md border-sky-200 bg-sky-50 py-2 pr-3 text-sm text-sky-950 sm:w-auto dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-50"
                                                            data-testid="bulk-bundles-filtered-view-hint"
                                                            role="status"
                                                        >
                                                            <Info
                                                                aria-hidden
                                                                className="text-sky-600 dark:text-sky-400"
                                                            />
                                                            <AlertDescription className="text-sky-900 dark:text-sky-100">
                                                                Showing only rows with validation issues, warnings,
                                                                pending edits, or rows marked for deletion. Turn off the
                                                                switch to see all rows.
                                                            </AlertDescription>
                                                        </Alert>
                                                    )}
                                                    {bulkUpload.showIssuesRowFilter && (
                                                        <BulkUploadRowFilterSwitch
                                                            checked={bulkUpload.showIssuesOnly}
                                                            disabled={bulkUpload.rows.length === 0}
                                                            id="bulk-bundles-show-issues-only"
                                                            onCheckedChange={bulkUpload.setShowIssuesOnly}
                                                        />
                                                    )}
                                                </div>
                                                {bulkUpload.hasUnsavedChanges && (
                                                    <>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={bulkUpload.clearPendingChanges}
                                                        >
                                                            Discard Changes
                                                        </Button>
                                                        <Button
                                                            variant="default"
                                                            size="sm"
                                                            onClick={bulkUpload.submitChanges}
                                                        >
                                                            Submit Changes
                                                        </Button>
                                                    </>
                                                )}
                                                <Button variant="outline" size="sm" onClick={handleStartOver}>
                                                    Start Over
                                                </Button>
                                                <Button
                                                    variant="default"
                                                    size="sm"
                                                    onClick={handlePersist}
                                                    disabled={!bulkUpload.canPersist}
                                                >
                                                    Save Bundles
                                                </Button>
                                            </div>
                                        </div>
                                        <BulkUploadTable
                                            columnDefs={BUNDLE_COLUMN_DEFS}
                                            fillAvailableHeight
                                            rows={bulkUpload.tableRows}
                                            errorsByCell={bulkUpload.errorsByCell}
                                            pendingEdits={bulkUpload.pendingEdits}
                                            updateCell={bulkUpload.updateCell}
                                            pendingDeletes={bulkUpload.pendingDeletes}
                                            toggleRowDelete={bulkUpload.toggleRowDelete}
                                        />
                                        <BulkUploadPendingChanges
                                            pendingDeletes={bulkUpload.pendingDeletes}
                                            pendingEdits={bulkUpload.pendingEdits}
                                            rowsLength={bulkUpload.rows.length}
                                        />
                                    </div>
                                )}

                            {!source && (
                                <p className="text-sm text-muted-foreground">
                                    Please select a partner account above to upload files.
                                </p>
                            )}
                            {source && (!isValidWeek(week) || !isValidYear(year)) && (
                                <p className="text-sm text-muted-foreground">
                                    Please enter a valid week (1-53) and year.
                                </p>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}

export { BulkBundlesUpload };
